import React, { useCallback, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { logger as LOG } from '@garnish/logger';

import { HostedFrame, type HostedFrameRef, IllusDressing_1 } from '@sg/garnish';
import type { OrderType } from '@sg/graphql-schema';

import { LoadingAnimation } from '../../components';
import { useMomoSurveyMessages, useSaveFeedbackCase } from './hooks';
import { LandingContent } from './RateOrderScreen.landing';

// ─────────────────────────────────────────────────────────────────────────────

type MomosSurveyContentProps = Readonly<{
  url: string;
  orderId: string;
  orderType: OrderType;
  onCompleted: () => void;
}>;

export const MomosSurveyContent = ({
  url,
  orderId,
  orderType,
  onCompleted,
}: MomosSurveyContentProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const { formatMessage } = useIntl();
  const frameRef = useRef<HostedFrameRef>(null);

  const { saveFeedbackCase } = useSaveFeedbackCase();

  const handleLoadEnd = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  const handleMomoMessage = useCallback(
    async (data: unknown) => {
      const payload = parseMomoMessage(data);

      logger.info('received message', { data, payload });

      if (!isSurveySubmitEvent(payload)) return;

      const result = await saveFeedbackCase({
        orderId,
        orderType,
        isFeedbackComplete: true,
      });

      if (result?.error) {
        logger.error('saveFeedbackCase failed', result.error);
        return;
      }

      onCompleted();
    },
    [orderId, orderType, saveFeedbackCase, onCompleted],
  );

  useMomoSurveyMessages({
    frameRef,
    onMessage: handleMomoMessage,
    enabled: !hasError,
  });

  return hasError ? (
    <LandingContent>
      <LandingContent.Illustration source={IllusDressing_1} style={styles.errorIllustration} />

      <LandingContent.Title>{formatMessage(messages.errorTitle)}</LandingContent.Title>

      <LandingContent.Subtitle>{formatMessage(messages.errorSubtitle)}</LandingContent.Subtitle>
    </LandingContent>
  ) : (
    <View style={styles.container}>
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <LoadingAnimation />
        </View>
      )}

      <HostedFrame
        ref={frameRef}
        frameId="momos-rate-order-survey"
        frameHeight="100%"
        containerStyle={styles.frameContainer}
        source={{ uri: url }}
        isLoading={isLoading}
        onLoadEnd={handleLoadEnd}
        onError={handleError}
        sharedCookiesEnabled
        thirdPartyCookiesEnabled
      />
    </View>
  );
};

// ─── Message parsing ─────────────────────────────────────────────────────────

const parseMomoMessage = (data: unknown): Record<string, unknown> | null => {
  if (typeof data === 'object' && data !== null) {
    return data as Record<string, unknown>;
  }

  if (typeof data !== 'string' || !data) return null;

  try {
    const parsed = JSON.parse(data) as unknown;

    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

const isSurveySubmitEvent = (payload: Record<string, unknown> | null): boolean => {
  if (!payload) return false;

  return payload.eventName === 'Submit' && payload.eventType === 'Click';
};

// ─── Logger ──────────────────────────────────────────────────────────────────

const logger = LOG.extend('MomoSurvey');

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  errorTitle: {
    defaultMessage: `We couldn't load this survey.`,
    description: 'Rate | Order | Momos Survey | Error View | Title',
  },
  errorSubtitle: {
    defaultMessage: 'Please try again later.',
    description: 'Rate | Order | Momos Survey | Error View | Subtitle',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
  },
  frameContainer: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorIllustration: {
    width: 200,
    height: 200,
  },
});
